import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail as MailIcon, MessageCircle as WhatsIcon } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import api, { formatApiError } from "@/lib/api";
import { trackSignup, trackLead } from "@/lib/analyticsEvents";
import { activeChannels } from "@/lib/contactChannels";
import { isAppOrWebLink } from "@/components/ContactChannels";
import { toast } from "sonner";
import { BrandIcon } from "@/components/icons/BrandIcons";
import BrandLogo from "@/components/icons/BrandLogo";

// Social key → brand icon mapping. Key matches site.social.* keys.
const SOCIAL_TO_BRAND = {
  facebook: "facebook",
  instagram: "instagram",
  youtube: "youtube",
  tiktok: "tiktok",
  telegram: "telegram",
  whatsapp: "whatsapp",
  whatsapp_community: "whatsapp",
  spotify: "spotify",
  linkedin: "linkedin",
  x: "x",
  twitter: "x",
  threads: "threads",
  pinterest: "pinterest",
  discord: "discord",
};

const SOCIAL_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  whatsapp_community: "WhatsApp",
  spotify: "Spotify",
  linkedin: "LinkedIn",
  x: "X",
  twitter: "X",
  threads: "Threads",
  pinterest: "Pinterest",
  discord: "Discord",
};

const DEFAULT_LEGAL = [
  { label: "Termeni", url: "/termeni" },
  { label: "Confidențialitate", url: "/politica-confidentialitate" },
  { label: "Cookies", url: "/politica-cookies" },
  { label: "GDPR", url: "/gdpr" },
  { label: "Disclaimer", url: "/disclaimer" },
];

const DEFAULT_NAV = [
  { label: "Despre mine", url: "/despre-mine" },
  { label: "Ședințe", url: "/sedinte" },
  { label: "Tehnici", url: "/tehnici" },
  { label: "Servicii", url: "/servicii" },
  { label: "Testimoniale", url: "/recenzii" },
  { label: "Contact", url: "/contact" },
];

export default function Footer() {
  const { site } = useSite();
  const brand = site?.brand?.name || "Terapeut Cristian";
  const footer = site?.footer || {};
  const tagline = footer.tagline || "Eliberare · Vindecare · Reamintire";
  const intro = footer.intro || "Ghid pentru eliberare emoțională, reprogramare mentală și transformare interioară profundă.";
  const social = site?.social || {};
  const contact = site?.contact || {};
  const disclaimer = site?.disclaimer || "";
  // F.4 (cta-spec.md): linkul WhatsApp din footer trece acum prin helperul comun
  // (activeChannels/buildChannelHref) — respectă `enabled` și configurarea reală
  // a canalului, nu doar `contact.whatsapp_number`. Câmpul vechi rămâne fallback
  // pentru situația în care niciun canal „whatsapp" nu e configurat/activ încă.
  const waChannel = activeChannels(site).find((c) => c.type === "whatsapp") || null;

  const navTitle = footer.navigation_title || "Navigare";
  const navLinks = (Array.isArray(footer.navigation_links) && footer.navigation_links.length > 0)
    ? footer.navigation_links
    : DEFAULT_NAV;
  const newsletterTitle = footer.newsletter_title || "Newsletter";
  const newsletterSubtitle = footer.newsletter_subtitle || "Primește, din când în când, un gând de claritate și blândețe direct în inbox.";
  const followTitle = footer.follow_title || "Urmărește";
  const legalLinks = (Array.isArray(footer.legal_links) && footer.legal_links.length > 0)
    ? footer.legal_links
    : DEFAULT_LEGAL;
  const copyrightTemplate = footer.copyright_template || "© {year} {brand}. Toate drepturile rezervate.";
  const showAnpc = footer.show_anpc !== false;

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false); // consimțământ marketing explicit (GDPR)
  const [submitting, setSubmitting] = useState(false);
  const [gift, setGift] = useState(null); // cadoul la abonare (setat de Cristian în admin)

  const subscribe = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Bifează consimțământul ca să te putem abona.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/newsletter", { email, consent });
      toast.success(data?.message || "Te-ai abonat cu succes.");
      trackSignup("newsletter");
      if (data?.gift?.url) setGift(data.gift);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const initials = brand.split(" ").map((p) => p[0]).slice(0, 2).join("");
  const activeSocials = Object.entries(social).filter(([_, v]) => v);

  const copyrightText = copyrightTemplate
    .replace("{year}", new Date().getFullYear())
    .replace("{brand}", brand);

  return (
    <footer
      data-testid="site-footer"
      style={{ background: "var(--ct-footer-bg)", color: "var(--ct-footer-text)" }}
    >
      {/* MAIN GRID: 1.4fr / 1fr / 1fr / 1fr (per design spec) */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-12 py-12 lg:py-14 grid grid-cols-1 md:grid-cols-2 lg:[grid-template-columns:1.4fr_1fr_1fr_1fr] gap-10 lg:gap-12">
        {/* Brand column */}
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <BrandLogo size={48} variant="gold" />
            <div>
              <h3 className="font-serif text-2xl" style={{ color: "var(--ct-footer-text)" }}>{brand}</h3>
              <span className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "var(--ct-gold)" }}>{tagline}</span>
            </div>
          </div>
          <p className="text-sm leading-relaxed max-w-md" style={{ color: "var(--ct-footer-muted)" }}>{intro}</p>
          {waChannel ? (
            <a
              href={waChannel.href}
              {...(isAppOrWebLink(waChannel.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={() => trackLead("footer_whatsapp")}
              data-testid="footer-whatsapp"
              className="inline-flex items-center gap-2 text-sm hover:text-[var(--ct-gold)]"
              style={{ color: "var(--ct-footer-text)" }}
            >
              <WhatsIcon size={16} /> {waChannel.label || "WhatsApp"}
            </a>
          ) : contact.whatsapp_number && !/^(40)?70{6,}\d*$/.test(contact.whatsapp_number.replace(/\D/g, "")) && (
            <a href={`https://wa.me/${contact.whatsapp_number.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 text-sm hover:text-[var(--ct-gold)]" style={{ color: "var(--ct-footer-text)" }}>
              <WhatsIcon size={16} /> WhatsApp: +{contact.whatsapp_number}
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}`} data-testid="footer-email" className="block text-sm hover:text-[var(--ct-gold)]" style={{ color: "var(--ct-footer-text)" }}>
              <span className="inline-flex items-center gap-2"><MailIcon size={16} /> {contact.email}</span>
            </a>
          )}
        </div>

        {/* Navigation column */}
        <div>
          <h4 className="text-sm uppercase tracking-[0.22em] mb-5" style={{ color: "var(--ct-gold)" }}>{navTitle}</h4>
          <ul className="space-y-3 text-sm" style={{ color: "var(--ct-footer-muted)" }}>
            {navLinks.map((l, i) => (
              <li key={`${l.url}-${i}`}>
                <Link to={l.url} className="hover:text-[var(--ct-gold)]" data-testid={`footer-nav-${i}`}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter column */}
        <div>
          <h4 className="text-sm uppercase tracking-[0.22em] mb-5" style={{ color: "var(--ct-gold)" }}>{newsletterTitle}</h4>
          <p className="text-xs mb-3" style={{ color: "var(--ct-footer-muted)" }}>{newsletterSubtitle}</p>
          <form onSubmit={subscribe} className="flex flex-col gap-2">
            <label htmlFor="newsletter-email" className="sr-only">Adresa ta de email</label>
            <input
              id="newsletter-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplu.ro"
              className="text-sm px-3 py-2.5 rounded-lg border"
              style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.14)", color: "var(--ct-footer-text)" }}
              data-testid="newsletter-email-input"
            />
            <label className="flex items-start gap-2 text-[11px] leading-snug cursor-pointer" style={{ color: "var(--ct-footer-muted)" }}>
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 accent-[var(--ct-gold)]"
                data-testid="newsletter-consent"
              />
              Sunt de acord să primesc emailuri și cu prelucrarea datelor conform Politicii de confidențialitate.
            </label>
            <button type="submit" disabled={submitting} className="text-sm px-4 py-2.5 rounded-lg font-medium" style={{ background: "var(--ct-gold)", color: "#0D1B2A" }} data-testid="newsletter-submit">
              {submitting ? "..." : "Abonează-mă"}
            </button>
          </form>
          {gift?.url && (
            <a href={gift.url} target="_blank" rel="noreferrer" className="block mt-3 text-sm px-4 py-2.5 rounded-lg text-center font-medium" data-testid="newsletter-gift"
              style={{ border: "1px solid var(--ct-gold)", color: "var(--ct-gold)" }}>
              🎁 Cadoul tău: {gift.title || "descarcă aici"}
            </a>
          )}
        </div>

        {/* Follow column — OFFICIAL BRAND ICONS, circular; ascunsă complet fără rețele configurate */}
        {activeSocials.length > 0 && (
        <div>
          <h4 className="text-sm uppercase tracking-[0.22em] mb-5" style={{ color: "var(--ct-gold)" }}>{followTitle}</h4>
          {(
            <div className="flex flex-wrap gap-3">
              {activeSocials.map(([k, url]) => {
                const brandKey = SOCIAL_TO_BRAND[k] || k;
                const label = SOCIAL_LABELS[k] || k;
                return (
                  <a
                    key={k}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`social-${k}`}
                    title={label}
                    aria-label={label}
                    className="w-[38px] h-[38px] rounded-full flex items-center justify-center transition-transform hover:scale-110"
                    style={{ border: "1px solid rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.04)" }}
                  >
                    <BrandIcon kind={brandKey} size={18} color="#FFFFFF" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>

      {/* DISCLAIMER + LEGAL LINKS row */}
      <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="max-w-[1440px] mx-auto px-4 lg:px-12 py-6 grid md:grid-cols-2 gap-6">
          {disclaimer && (
            <p className="text-xs leading-relaxed" style={{ color: "var(--ct-footer-muted)" }}>
              {disclaimer}
            </p>
          )}
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs md:justify-end items-start" style={{ color: "var(--ct-footer-muted)" }}>
            {legalLinks.map((l, i) => (
              <Link key={`${l.url}-${i}`} to={l.url} className="hover:text-[var(--ct-gold)]" data-testid={`footer-legal-${i}`}>{l.label}</Link>
            ))}
          </div>
        </div>

        {/* ANPC SAL — image only, no extra labels */}
        {showAnpc && (
          <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="anpc-row">
            <div className="max-w-[1440px] mx-auto px-4 lg:px-12 py-5 flex justify-center md:justify-end">
              <a
                href="https://reclamatiisal.anpc.ro/"
                target="_blank"
                rel="noreferrer"
                title="ANPC — Soluționarea Alternativă a Litigiilor"
                data-testid="anpc-sal-link"
                className="inline-block transition-opacity hover:opacity-85"
              >
                <img
                  src="/assets/anpc-sal.svg"
                  alt="ANPC SAL"
                  className="h-12 w-auto"
                  style={{ maxWidth: "200px" }}
                />
              </a>
            </div>
          </div>
        )}

        <div className="text-center pb-6 text-xs" style={{ color: "var(--ct-footer-muted)" }}>
          {copyrightText}
        </div>
      </div>
    </footer>
  );
}
